import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFinanceRecords } from '../../shared/api/api';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import FilterDropdown from '../../shared/components/FilterDropdown';
import Pagination from '../../shared/components/Pagination';
import usePagination from '../../shared/hooks/usePagination';
import useResizableColumns from '../../shared/hooks/useResizableColumns';
import '../../shared/styles/resizableTable.css';
import './FinanceDashboardPage.css';

const FD_COLUMNS = [
  { key: 'userId', label: 'User', width: 160 },
  { key: 'tenantId', label: 'Tenant', width: 140 },
  { key: 'division', label: 'Division', width: 140 },
  { key: 'qisGenerations', label: 'QIS Generations', width: 150, num: true },
  { key: 'totalRequests', label: 'Total Requests', width: 140, num: true },
  { key: 'totalTokens', label: 'Tokens Used', width: 130, num: true },
  { key: 'totalCostUsd', label: 'Total Cost', width: 120, num: true },
];

function FinanceDashboardPage() {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const { colWidths, tableWidth, handleResizeStart } = useResizableColumns(FD_COLUMNS);

  const [tenantFilter, setTenantFilter] = useState([]);
  const [userFilter, setUserFilter] = useState([]);
  const [featureFilter, setFeatureFilter] = useState([]);
  const [subFeatureFilter, setSubFeatureFilter] = useState([]);

  const [sortField, setSortField] = useState('qisGenerations');
  const [sortDir, setSortDir] = useState('desc');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getFinanceRecords();
      const data = res.data;
      setRecords(Array.isArray(data) ? data : data?.records || []);
      setLastUpdated(new Date());
    } catch {
      setError('Failed to load finance data.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Derive filter options from real data
  const tenantOptions = useMemo(
    () => [...new Set(records.map((r) => r.tenantId).filter(Boolean))].sort(),
    [records]
  );
  const userOptions = useMemo(
    () => [...new Set(records.map((r) => r.userId).filter(Boolean))].sort(),
    [records]
  );
  const featureOptions = useMemo(
    () => [...new Set(records.map((r) => r.featureName).filter(Boolean))].sort(),
    [records]
  );
  const subFeatureOptions = useMemo(
    () => [...new Set(records.map((r) => r.subFeature).filter(Boolean))].sort(),
    [records]
  );

  // Aggregate stat cards from records
  const stats = useMemo(() => {
    const filtered = records
      .filter((r) => !tenantFilter.length || tenantFilter.includes(r.tenantId))
      .filter((r) => !userFilter.length || userFilter.includes(r.userId));

    const uniqueUsers = new Set(filtered.map((r) => r.userId)).size;
    const qisGens = filtered.filter((r) => r.featureName === 'QIS').length;
    const totalCost = filtered.reduce((s, r) => s + (r.totalCostUsd || 0), 0);
    const totalTokens = filtered.reduce((s, r) => s + (r.totalTokens || 0), 0);
    const conversations = new Set(filtered.map((r) => r.conversationId)).size;

    return { uniqueUsers, qisGens, totalCost, totalTokens, conversations };
  }, [records, tenantFilter, userFilter]);

  // Aggregate usage table by user
  const usageByUser = useMemo(() => {
    const filtered = records
      .filter((r) => !tenantFilter.length || tenantFilter.includes(r.tenantId))
      .filter((r) => !userFilter.length || userFilter.includes(r.userId))
      .filter((r) => !featureFilter.length || featureFilter.includes(r.featureName))
      .filter((r) => !subFeatureFilter.length || subFeatureFilter.includes(r.subFeature));

    const map = {};
    filtered.forEach((r) => {
      if (!map[r.userId]) {
        map[r.userId] = {
          userId: r.userId,
          tenantId: r.tenantId,
          division: r.division,
          qisGenerations: 0,
          totalRequests: 0,
          totalCostUsd: 0,
          totalTokens: 0,
          lastSubFeature: '',
        };
      }
      const u = map[r.userId];
      u.totalRequests += 1;
      u.totalCostUsd += r.totalCostUsd || 0;
      u.totalTokens += r.totalTokens || 0;
      if (r.featureName === 'QIS') u.qisGenerations += 1;
      u.lastSubFeature = r.subFeature || u.lastSubFeature;
    });
    return Object.values(map);
  }, [records, tenantFilter, userFilter, featureFilter, subFeatureFilter]);

  const hasFilters = tenantFilter.length || userFilter.length || featureFilter.length || subFeatureFilter.length;
  const clearFilters = () => {
    setTenantFilter([]); setUserFilter([]); setFeatureFilter([]); setSubFeatureFilter([]);
  };

  const sorted = useMemo(() => {
    return [...usageByUser].sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [usageByUser, sortField, sortDir]);

  const { currentPage, pageSize, totalPages, paginated, setCurrentPage, setPageSize, resetPage } =
    usePagination(sorted, 15);

  useEffect(() => { resetPage(); }, [tenantFilter, userFilter, featureFilter, subFeatureFilter, resetPage]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return <span className="fd-sort-icon">⇅</span>;
    return <span className="fd-sort-icon">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const fmtNum = (n) => (n != null ? Number(n).toLocaleString() : '—');
  const fmtCost = (n) => (n != null ? '$' + Number(n).toFixed(4) : '—');

  const statCards = [
    { label: 'Active Users', value: fmtNum(stats.uniqueUsers), mod: '' },
    { label: 'QIS Generations', value: fmtNum(stats.qisGens), mod: 'primary' },
    { label: 'Conversations', value: fmtNum(stats.conversations), mod: '' },
    { label: 'Total Tokens Used', value: fmtNum(stats.totalTokens), mod: '' },
    { label: 'Total Cost', value: fmtCost(stats.totalCost), mod: 'success' },
  ];

  return (
    <div className="page-container fd-page">
      {loading && <LoadingSpinner message="Loading finance dashboard..." />}

      <div className="fd-header">
        <div>
          <h1 className="page-title fd-title">Finance Dashboard</h1>
          {lastUpdated && (
            <span className="fd-last-updated">Last updated: {lastUpdated.toLocaleTimeString()}</span>
          )}
        </div>
        <button className="btn btn-secondary fd-refresh-btn" onClick={fetchAll} disabled={loading}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
            <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Refresh
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Stat cards */}
      <div className="fd-stats-row">
        {statCards.map((s) => (
          <div key={s.label} className={`fd-stat-card ${s.mod ? `fd-stat-card--${s.mod}` : ''}`}>
            <span className="fd-stat-value">{s.value}</span>
            <span className="fd-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="fd-filter-bar">
        <FilterDropdown label="All Tenants" options={tenantOptions} selected={tenantFilter} onChange={setTenantFilter} />
        <FilterDropdown label="All Users" options={userOptions} selected={userFilter} onChange={setUserFilter} />
        <FilterDropdown
          label="All Features"
          options={featureOptions.length ? featureOptions : ['QIS', 'Document Generation', 'Execution', 'Project Management']}
          selected={featureFilter}
          onChange={setFeatureFilter}
        />
        <FilterDropdown
          label="All Sub-Features"
          options={subFeatureOptions.length ? subFeatureOptions : ['Generate FR', 'Generate Non-FR', 'Traceability Matrix', 'Functional Spec']}
          selected={subFeatureFilter}
          onChange={setSubFeatureFilter}
        />
        {hasFilters ? (
          <button className="fd-clear-btn" onClick={clearFilters}>Clear filters</button>
        ) : null}
      </div>

      {/* Usage by user table */}
      <div className="card fd-table-card">
        <div className="fd-table-toolbar">
          <span className="fd-table-count">{sorted.length.toLocaleString()} users</span>
          <button className="btn btn-secondary fd-export-btn" onClick={() => navigate('/finance/records')}>
            View Records →
          </button>
        </div>
        <div className="fd-table-wrapper">
          <table className="fd-table resizable-table" style={{ width: tableWidth }}>
            <colgroup>
              {FD_COLUMNS.map((c) => (
                <col key={c.key} style={{ width: `${colWidths[c.key]}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {FD_COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className={`resizable-th fd-th-sortable${c.num ? ' fd-th-num' : ''}`}
                    onClick={() => handleSort(c.key)}
                  >
                    {c.label} {sortIcon(c.key)}
                    <span className="col-resize-handle" onMouseDown={handleResizeStart(c.key)} onClick={(e) => e.stopPropagation()} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="fd-empty">
                    {records.length === 0
                      ? 'No finance data available yet — waiting for the API to return records.'
                      : 'No records match the current filters.'}
                  </td>
                </tr>
              ) : (
                paginated.map((u) => (
                  <tr key={u.userId}>
                    <td className="fd-cell-user">{u.userId}</td>
                    <td>{u.tenantId || '—'}</td>
                    <td>{u.division || '—'}</td>
                    <td className="fd-cell-num">{fmtNum(u.qisGenerations)}</td>
                    <td className="fd-cell-num">{fmtNum(u.totalRequests)}</td>
                    <td className="fd-cell-num">{fmtNum(u.totalTokens)}</td>
                    <td className="fd-cell-num fd-cell-cost">{fmtCost(u.totalCostUsd)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={sorted.length}
        displayedItems={paginated.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}

export default FinanceDashboardPage;
